import { useMemo } from "react";
import {
  GameCommandByPlayer,
  LobbyCommand,
  LobbyState,
  LobbySyncAdapterProps,
  lobbyStateManagerFactory,
  useStateManager,
} from "../../infra";
import { useSessionUser } from "../hook/useSession";
import { contextFactory } from "../context/contextFactory";

export type LobbyContext = {
  state: LobbyState;
  update: (command: Omit<LobbyCommand, "sender">) => void;
  deleteState: () => void;
  syncStatus: ReturnType<typeof useStateManager>["status"];
};

const emptyState = (): LobbyState => {
  return { games: [] };
};

export const lobbyContextFactory = ({
  lobbySyncAdapterProps,
}: {
  lobbySyncAdapterProps: LobbySyncAdapterProps;
}) => {
  const { Provider: LobbyProvider, useCustomContext: useLobby } =
    contextFactory<LobbyContext>(function lobbyContext() {
      const sender = useSessionUser();
      const { pollingSync } = useMemo(
        () => lobbyStateManagerFactory(emptyState(), lobbySyncAdapterProps),
        []
      );
      const { status: syncStatus } = useStateManager(pollingSync);
      const { state } = pollingSync;

      async function update(command: Omit<LobbyCommand, "sender">) {
        const commandBySender: GameCommandByPlayer<LobbyCommand> = {
          ...command,
          sender,
          playerId: sender.id,
        };
        pollingSync.send(commandBySender);
      }

      const deleteState = async () => {
        await pollingSync.delete();
      };
      return {
        state,
        update,
        deleteState,
        syncStatus,
      };
    });
  return { LobbyProvider, useLobby };
};
